import { getReports } from "@/api/gen-api";
import { useReportStatusSocket } from "@/hooks/use-report-status-socket";
import { founderInvitationApi } from "@/api/deals-api";
import { DealDetailSkeleton } from "@/components/deal-detail/deal-detail-skeleton";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealsListStore } from "@/store/deals-list-store";
import { TOUR_TYPES, useTourStore } from "@/store/tour-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router";
import { DiligencegptSidebar } from "../sidebar/diligencegpt-sidebar";
import { BaseLayout } from "./base-layout";

type DealInfoItem = {
  field_name: string;
  field_value: string | number | string[];
  field_state: string;
  sources?: Array<{
    text: string;
    created_on: string;
    file_type: string;
    source_type: string;
    heading: string;
    file_url: string;
    category: string[];
    source_description: string;
  }>;
};

interface WebsiteSummaryData {
  website_summary: string;
  created_on?: Date;
}

type ApiResponse = {
  status: string;
  data: {
    summaries?: Array<{
      _id: string;
      summary: {
        // Backward compatible: supports both old format (string) and new format (object with generation and sources)
        Idea?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        Tech?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        Marketing?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        Team?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        BusinessModel?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        Traction?:
          | string
          | {
              generation: string;
              sources?: Array<{
                text: string;
                created_on: string;
                file_type: string;
                source_type: string;
                heading: string;
                file_url: string;
                category: string[];
                source_description: string;
              }>;
            };
        // Allow for _id field that might be present
        _id?: string;
      };
      created_on: Date;
      credits_used: number;
    }>;
    tearsheets?: Array<{
      _id: string;
      tearsheet: {
        Overview: string;
        Problem: string;
        Solution: string;
        Market: string;
        Traction: string;
        Advantage: string;
        KeyHighlights: string[];
        DateCreated: string;
      };
      created_on: Date;
      credits_used: number;
    }>;
    startupscores?: Array<{
      _id: string;
      startupscore: {
        Idea: { Score: number; Reason: string };
        Tech: { Score: number; Reason: string };
        Team: { Score: number; Reason: string };
        Marketing: { Score: number; Reason: string };
        Traction: { Score: number; Reason: string };
        Overall: { Score: number; Reason: string };
      };
      created_on: Date;
      credits_used: number;
    }>;
    datasufficiencyscores?: Array<{
      _id: string;
      datasufficiencyscore: {
        Idea: { Score: number; Reason: string };
        Tech: { Score: number; Reason: string };
        Team: { Score: number; Reason: string };
        Marketing: { Score: number; Reason: string };
        Traction: { Score: number; Reason: string };
        Overall: { Score: number; Reason: string };
      };
      created_on: Date;
      credits_used: number;
    }>;
    metrics?: Array<{
      _id: string;
      metrics: Array<{
        title: string;
        sub_category: string;
        category: string;
        sources: Array<{
          file_url: string;
          file_type: string;
          source_type: string;
          source_description: string;
        }>;
        data: Array<{
          title: string;
          value: string | number;
        }>;
      }>;
      created_on: Date;
      credits_used?: number;
    }>;
    dealInfo?: Array<{
      _id: string;
      deal: DealInfoItem[];
      created_on: Date;
      credits_used: number;
    }>;
    websiteInfo?: WebsiteSummaryData;
  };
};

type SummaryItem = NonNullable<ApiResponse["data"]["summaries"]>[0];
type TearsheetItem = NonNullable<ApiResponse["data"]["tearsheets"]>[0];
type StartupscoreItem = NonNullable<ApiResponse["data"]["startupscores"]>[0];
type DatasufficiencyItem = NonNullable<
  ApiResponse["data"]["datasufficiencyscores"]
>[0];
type MetricsReport = NonNullable<ApiResponse["data"]["metrics"]>[0];

export const DealsLayout = () => {
  const { id } = useParams();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { activeWorkspace } = useWorkspaceStore();
  const processedInitialData = useRef<Set<string>>(new Set());

  // Founder invitation state
  const [invitationStatus, setInvitationStatus] = useState<{
    hasInvitation: boolean;
    invitation: any;
    companyName: string;
  } | null>(null);

  // Track which deals have been initially loaded
  const [initiallyLoadedDeals, setInitiallyLoadedDeals] = useState<Set<string>>(
    new Set()
  );

  const {
    currentDeal,
    isLoading: isDealsLoading,
    updateDeal,
    fetchDeal,
    clearDeal,
  } = useCurrentDealStore();

  const dealsListDeals = useDealsListStore((state) => state.deals);
  const fetchDealsList = useDealsListStore((state) => state.fetchDeals);
  const dealsListUpdateDeal = useDealsListStore((state) => state.updateDeal);

  // Ensure deals list store is populated on deal detail pages
  // (it's normally only fetched on the deals listing page)
  useEffect(() => {
    if (activeWorkspace?._id && Object.keys(dealsListDeals).length === 0) {
      fetchDealsList(activeWorkspace._id);
    }
  }, [activeWorkspace?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top when route changes within deals layout
  useEffect(() => {
    const scrollToTop = () => {
      // Scroll window if needed
      if (window.scrollY > 0) {
        window.scrollTo(0, 0);
      }

      // Scroll document element
      if (document.documentElement.scrollTop > 0) {
        document.documentElement.scrollTo({ top: 0, behavior: "instant" });
      }

      // Scroll main element and any overflow containers
      const mainElement = document.querySelector("main");
      const contentDivs = mainElement?.querySelectorAll(
        "div[class*='overflow']"
      ) as NodeListOf<HTMLElement>;

      if (mainElement && mainElement.scrollTop > 0) {
        mainElement.scrollTo({ top: 0, behavior: "instant" });
      }

      if (contentDivs) {
        contentDivs.forEach((div) => {
          if (div.scrollTop > 0) {
            div.scrollTo({ top: 0, behavior: "instant" });
          }
        });
      }
    };

    // Try immediately and after a short delay (in case content hasn't rendered yet)
    scrollToTop();
    const timeoutId = setTimeout(scrollToTop, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Fetch deal when id changes
  useEffect(() => {
    if (id && activeWorkspace?._id) {
      fetchDeal(activeWorkspace?._id, id);
    }
    return () => {
      clearDeal();
    };
  }, [id, activeWorkspace?._id, fetchDeal, clearDeal]);

  // Track previous report status in a ref (not state) so updating it inside
  // the reportData queryFn doesn't change that query's key. The earlier
  // state-in-key form updated previousStatus in the same render cycle as
  // reportStatus, so react-query could run only the latest-keyed fetch (where
  // previous already equals current → no transition) and skip the one that
  // would pull the just-completed report — leaving it to a later trigger.
  const previousStatusRef = useRef<Record<string, string>>({});

  // Fetch all reports when deal is first loaded
  const { data: initialReportData } = useQuery({
    queryKey: [
      "initialReportData",
      currentDeal?.id,
      activeWorkspace?._id,
      initiallyLoadedDeals,
    ],
    queryFn: async () => {
      if (
        !currentDeal?.id ||
        !activeWorkspace?._id ||
        initiallyLoadedDeals.has(currentDeal.id)
      ) {
        return null;
      }

      try {
        const allReports = await getReports(
          currentDeal.id,
          activeWorkspace._id
        );
        console.log("Initial reports fetched:", allReports);

        // Mark this deal as initially loaded
        setInitiallyLoadedDeals((prev) => new Set([...prev, currentDeal.id]));

        return allReports;
      } catch (error) {
        console.error("Error fetching initial reports:", error);
        return null;
      }
    },
    enabled:
      !!currentDeal?.id &&
      !!activeWorkspace?._id &&
      !initiallyLoadedDeals.has(currentDeal.id),
  });

  // Process initial report data
  useEffect(() => {
    if (!initialReportData?.data || !currentDeal?.id) return;

    // Prevent re-processing for the same deal to avoid loops
    if (processedInitialData.current.has(currentDeal.id)) {
      return;
    }

    // Batch all updates into a single object
    const updates: {
      aiGenerations?: Record<
        string,
        Array<
          | SummaryItem
          | TearsheetItem
          | StartupscoreItem
          | DatasufficiencyItem
          | MetricsReport
        >
      >;
      dealInfo?: { deal: DealInfoItem[] };
      overview?: { websiteSummary?: Omit<WebsiteSummaryData, "created_on"> };
    } = {};

    // Process all report types from the clean structure
    Object.entries(initialReportData.data).forEach(([type, items]) => {
      // Skip dealInfo, websiteInfo, and linkedInInfo as they're handled separately
      if (
        type === "dealInfo" ||
        type === "websiteInfo" ||
        type === "linkedInInfo"
      ) {
        return;
      }

      if (!items || !Array.isArray(items) || items.length === 0) return;

      const formattedItems = items.filter(Boolean).map((item: any) => ({
        ...item,
      }));

      if (formattedItems.length > 0) {
        const saveKey =
          type === "datasufficiencyscores"
            ? "datasufficiency"
            : type === "datasufficiencyscores_v2"
            ? "datasufficiency_v2"
            : type;
        if (!updates.aiGenerations) updates.aiGenerations = {};
        updates.aiGenerations[saveKey] = formattedItems as Array<
          | SummaryItem
          | TearsheetItem
          | StartupscoreItem
          | DatasufficiencyItem
          | MetricsReport
        >;
      }
    });

    // Handle dealInfo separately
    if (
      initialReportData.data.dealInfo &&
      Array.isArray(initialReportData.data.dealInfo)
    ) {
      updates.dealInfo = {
        ...currentDeal.dealInfo,
        deal: initialReportData.data.dealInfo,
      };
    }

    // Handle websiteInfo separately
    if (initialReportData.data.websiteInfo) {
      console.log(
        "Processing websiteInfo:",
        initialReportData.data.websiteInfo
      );
      const websiteInfoData = initialReportData.data
        .websiteInfo as WebsiteSummaryData;

      const transformedWebsiteSummary = {
        website_summary: websiteInfoData.website_summary,
        created_on: websiteInfoData.created_on
          ? websiteInfoData.created_on instanceof Date
            ? websiteInfoData.created_on.toISOString()
            : new Date(websiteInfoData.created_on).toISOString()
          : undefined,
      };

      console.log("Transformed website summary:", transformedWebsiteSummary);

      if (!updates.overview) updates.overview = {};
      updates.overview.websiteSummary = transformedWebsiteSummary;
    }

    // Single update call with all changes
    if (Object.keys(updates).length > 0) {
      console.log("Updating deal with all initial data:", updates);

      // Mark as processed to break re-render loop
      processedInitialData.current.add(currentDeal.id);

      const finalUpdates: Partial<DealTransformed> = {};

      if (updates.aiGenerations) {
        finalUpdates.aiGenerations = {
          ...currentDeal.aiGenerations,
          ...updates.aiGenerations,
        };
      }

      if (updates.dealInfo) {
        finalUpdates.dealInfo = {
          ...currentDeal.dealInfo,
          ...updates.dealInfo,
        };
      }

      if (updates.overview) {
        finalUpdates.overview = {
          ...currentDeal.overview,
          ...updates.overview,
        };
      }

      updateDeal(finalUpdates);
    }
  }, [initialReportData, currentDeal, updateDeal]);

  // Report status via WebSocket (falls back to polling if socket is down)
  const { data: reportStatus } = useReportStatusSocket(
    currentDeal?.id,
    activeWorkspace?._id
  );

  // Fetch report data when generation is complete
  const { data: reportData } = useQuery({
    queryKey: [
      "reportData",
      currentDeal?.id,
      activeWorkspace?._id,
      reportStatus,
    ],
    queryFn: async () => {
      if (
        !currentDeal?.id ||
        !activeWorkspace?._id ||
        !reportStatus?.opportunityApplication?.report_status
      )
        return null;

      const currentStatus = reportStatus.opportunityApplication.report_status;

      // Map frontend report types to backend report types
      const reportTypeMapping = {
        summary: "summaries",
        tearsheet: "tearsheets",
        datasufficiencyscore: "datasufficiencyscores",
        datasufficiencyscore_v2: "datasufficiencyscores_v2",
        startupscore: "startupscores",
        deal_info: "dealInfo",
        metrics: "metrics",
        website_summary: "websiteInfo",
        website_summary_v2: "website_summary_v2",
        market_intel: "market_intel",
        market_research: "market_research",
        linkedin_company_scrape: "linkedInInfo",
        gap_analysis: "gap_analysis",
        deal_fit: "deal_fit",
        insights: "insights",
      } as const;

      // Find report types that have just completed OR have partial data ready.
      // Typed as `string[]` so the V2 tearsheet augmentation below (which
      // pushes a value outside the `as const` mapping union) typechecks.
      const newlyCompletedTypes: string[] = Object.entries(currentStatus)
        .filter(([type, status]) => {
          const prevStatus = previousStatusRef.current[type];
          const wasGenerating =
            prevStatus === "in_progress" ||
            prevStatus === "queued" ||
            (typeof prevStatus === "string" &&
              prevStatus.startsWith("generating_"));

          // For website_summary_v2, also fetch when transitioning to generating_secondary_webpage_info
          // (main page is done) or when completed
          if (type === "website_summary_v2") {
            const mainPageReady =
              prevStatus === "generating_main_webpage_info" &&
              status === "generating_secondary_webpage_info";
            const allComplete = wasGenerating && status === "completed";
            return (mainPageReady || allComplete) && type in reportTypeMapping;
          }

          // For all other reports, only fetch when completed
          return (
            wasGenerating && status === "completed" && type in reportTypeMapping
          );
        })
        .map(([type]) => {
          return reportTypeMapping[type as keyof typeof reportTypeMapping];
        });

      // Snapshot the status we just diffed against, so the next change compares
      // against it. Updated here (every tick, including the in_progress one)
      // rather than in an effect keyed off reportStatus, so it can't race the
      // query key — the later in_progress→completed transition is always seen.
      previousStatusRef.current = { ...currentStatus };

      // Tearsheet completion needs the V2 fetch too — V2 docs live in a
      // separate `tearsheets` collection that the legacy "tearsheets"
      // (V1) per-type fetch doesn't touch, so without this the V2 panel
      // would only refresh on a full page reload.
      if (
        newlyCompletedTypes.includes("tearsheets") &&
        !newlyCompletedTypes.includes("tearsheets_v2")
      ) {
        newlyCompletedTypes.push("tearsheets_v2");
      }

      if (newlyCompletedTypes.length === 0) return null;

      const completedReports = await Promise.all(
        newlyCompletedTypes.map(async (type) => {
          const report = await getReports(
            currentDeal.id,
            activeWorkspace._id,
            type
          );
          console.log(`Report for type ${type}:`, report);
          return { type, ...report };
        })
      );

      console.log("completed reports", completedReports);

      // Process and update deal immediately when reports are fetched
      const processedReports = completedReports
        .filter(Boolean)
        .reduce(
          (
            acc: Record<
              string,
              Array<
                | SummaryItem
                | TearsheetItem
                | StartupscoreItem
                | DatasufficiencyItem
                | MetricsReport
              >
            >,
            report
          ) => {
            if (!report.data) return acc;

            Object.entries(report.data).forEach(([dataKey, items]) => {
              // BE quirk: the V2 tearsheet collection is named "tearsheets"
              // on the backend, so getReports(..., "tearsheets_v2") returns
              // V2 docs keyed under "tearsheets" in the response. Without
              // this redirect the V2 items would fall into the legacy
              // formatting branch (which expects a `.tearsheet` field that
              // V2 docs don't have), overwrite aiGenerations.tearsheets
              // with mis-shaped entries, and leave aiGenerations.tearsheets_v2
              // untouched — so the unified `tearsheets_all` virtual key
              // never sees the new generation until a full deal refetch.
              const type =
                report.type === "tearsheets_v2" && dataKey === "tearsheets"
                  ? "tearsheets_v2"
                  : dataKey;
              // Special handling for websiteInfo since it's not an array
              if (type === "websiteInfo" && currentDeal.id) {
                // items is the websiteInfo object directly from the API
                const websiteInfoData = items as {
                  website_summary: string;
                  created_on?: Date;
                };

                // Check if the data has the expected structure
                if (!websiteInfoData || !websiteInfoData.website_summary) {
                  console.error(
                    "Invalid websiteInfo data structure:",
                    websiteInfoData
                  );
                  return;
                }

                const transformedWebsiteSummary = {
                  website_summary: websiteInfoData.website_summary,
                  created_on: websiteInfoData.created_on
                    ? websiteInfoData.created_on instanceof Date
                      ? websiteInfoData.created_on.toISOString()
                      : new Date(websiteInfoData.created_on).toISOString()
                    : undefined,
                };

                console.log(
                  "Transformed website summary:",
                  transformedWebsiteSummary
                );

                console.log(
                  "Before updateDeal - currentDeal.overview.websiteSummary:",
                  currentDeal.overview.websiteSummary
                );

                updateDeal({
                  overview: {
                    ...currentDeal.overview,
                    websiteSummary: transformedWebsiteSummary,
                  },
                });

                console.log("After updateDeal called");
                return;
              }

              // Special handling for linkedInInfo - it's an array like website_summary_v2
              if (type === "linkedInInfo" && currentDeal.id) {
                // items is an array of LinkedIn reports
                const linkedInInfoArray = items as Array<{
                  _id: string;
                  data: any;
                  timestamp?: Date | string;
                  created_on?: Date | string;
                }>;

                // Check if the data has the expected structure
                if (!Array.isArray(linkedInInfoArray)) {
                  console.error(
                    "Invalid linkedInInfo data structure:",
                    linkedInInfoArray
                  );
                  return;
                }

                // Transform the array to match the schema
                const transformedLinkedInReports = linkedInInfoArray.map(
                  (report) => ({
                    _id: report._id,
                    data: report.data,
                    created_on: report.created_on
                      ? report.created_on instanceof Date
                        ? report.created_on.toISOString()
                        : typeof report.created_on === "string"
                        ? report.created_on
                        : new Date(report.created_on).toISOString()
                      : report.timestamp
                      ? report.timestamp instanceof Date
                        ? report.timestamp.toISOString()
                        : typeof report.timestamp === "string"
                        ? report.timestamp
                        : new Date(report.timestamp).toISOString()
                      : new Date().toISOString(),
                  })
                );

                console.log(
                  "Transformed LinkedIn reports:",
                  transformedLinkedInReports
                );

                // Store LinkedIn data in aiGenerations (like website_summary_v2)
                // Merge with existing reports to avoid duplicates
                const existingReports =
                  currentDeal.aiGenerations.linkedInInfo || [];
                const existingIds = new Set(existingReports.map((r) => r._id));
                const newReports = transformedLinkedInReports.filter(
                  (r) => !existingIds.has(r._id)
                );

                // Always update if we have reports (even if no new ones, to ensure data is fresh)
                // This handles the case when there are no existing reports initially
                if (transformedLinkedInReports.length > 0) {
                  // If we have new reports, merge them. Otherwise, replace with all reports (handles initial load)
                  const reportsToStore =
                    newReports.length > 0
                      ? [...existingReports, ...newReports]
                      : transformedLinkedInReports;

                  updateDeal({
                    aiGenerations: {
                      ...currentDeal.aiGenerations,
                      linkedInInfo: reportsToStore,
                    },
                  });
                }

                console.log("After updateDeal called for LinkedIn info");
                return;
              }

              if (!items || !Array.isArray(items)) return;

              const formattedItems = items.map((item) => {
                const baseFormat = {
                  ...item,
                  ...("created_on" in item &&
                    item.created_on && {
                      created_on: new Date(item.created_on),
                    }),
                };

                console.log("Item structure:", JSON.stringify(item, null, 2));
                console.log("Current type in switch:", type);

                switch (type) {
                  case "summaries": {
                    console.log("Entering summaries case");
                    const summaryItem = item as unknown as SummaryItem;
                    return {
                      ...baseFormat,
                      summary: summaryItem.summary,
                    } as unknown as SummaryItem;
                  }
                  case "tearsheets": {
                    console.log("Entering tearsheets case");
                    const tearsheetItem = item as unknown as TearsheetItem;
                    if (!tearsheetItem.tearsheet)
                      return baseFormat as unknown as TearsheetItem;
                    return {
                      ...baseFormat,
                      tearsheet: {
                        ...tearsheetItem.tearsheet,
                        DateCreated:
                          tearsheetItem.tearsheet.DateCreated.toString(),
                      },
                    } as unknown as TearsheetItem;
                  }
                  case "startupscores": {
                    console.log("Entering startupscores case");
                    const startupscoreItem =
                      item as unknown as StartupscoreItem;
                    return {
                      ...baseFormat,
                      startupscore: startupscoreItem.startupscore,
                    } as unknown as StartupscoreItem;
                  }
                  case "datasufficiencyscores": {
                    console.log("Entering datasufficiency case");
                    const datasufficiencyItem =
                      item as unknown as DatasufficiencyItem;
                    console.log(
                      "Processing datasufficiency item:",
                      datasufficiencyItem
                    );
                    return {
                      ...baseFormat,
                      datasufficiencyscore:
                        datasufficiencyItem.datasufficiencyscore,
                    } as unknown as DatasufficiencyItem;
                  }
                  case "datasufficiencyscores_v2": {
                    console.log("Entering datasufficiency v2 case");
                    const datasufficiencyV2Item = item as unknown as {
                      _id: string;
                      datasufficiencyscore_v2: any;
                      created_on: string;
                      credits_used: number;
                    };
                    console.log(
                      "Processing datasufficiency v2 item:",
                      datasufficiencyV2Item
                    );
                    return {
                      ...baseFormat,
                      datasufficiencyscore_v2:
                        datasufficiencyV2Item.datasufficiencyscore_v2,
                    };
                  }
                  case "metrics": {
                    console.log("Entering metrics case");
                    const metricsReport = item as unknown as MetricsReport;
                    return {
                      ...baseFormat,
                      metrics: metricsReport.metrics,
                    } as unknown as MetricsReport;
                  }
                  case "website_summary_v2": {
                    console.log("Entering website_summary_v2 case");
                    return baseFormat;
                  }
                  case "tearsheets_v2": {
                    // V2 docs come from BE pre-shaped by reports.ts /
                    // dashboard.ts; just preserve the item (created_on is
                    // already converted by baseFormat above).
                    return baseFormat;
                  }
                  case "market_research": {
                    console.log("Entering market_research case");
                    return baseFormat;
                  }
                  case "dealInfo": {
                    console.log("Entering dealInfo case");
                    // Handle deal info updates
                    if (currentDeal.id) {
                      const dealInfoItems = (
                        items as unknown as Array<{
                          field_name: string;
                          field_value: string | number | string[];
                          field_state: string;
                          generations?: string | number | string[];
                          sources?: Array<{
                            text: string;
                            created_on: string;
                            file_type: string;
                            source_type: string;
                            heading: string;
                            file_url: string;
                            category: string[];
                            source_description: string;
                          }>;
                          suggestions?: Array<{
                            url: string;
                            title?: string;
                            description?: string;
                            favicon?: string;
                          }>;
                        }>
                      ).map((item) => {
                        // Default to generations if available, fallback to field_value
                        const fieldValue =
                          item.generations !== undefined &&
                          item.generations !== null &&
                          item.generations !== ""
                            ? item.generations
                            : item.field_value;

                        return {
                          field_name: item.field_name,
                          field_value: fieldValue,
                          field_state: item.field_state,
                          sources: item.sources as Array<{
                            text: string;
                            created_on: string;
                            file_type: string;
                            source_type: string;
                            heading: string;
                            file_url: string;
                            category: string[];
                            source_description: string;
                          }>,
                          suggestions: item.suggestions,
                        };
                      });

                      const websiteItem = dealInfoItems.find(
                        (item) =>
                          item.field_name === "company_website" &&
                          typeof item.field_value === "string"
                      );

                      const updates: Partial<DealTransformed> = {
                        dealInfo: {
                          ...currentDeal.dealInfo,
                          deal: dealInfoItems,
                        },
                      };

                      if (websiteItem) {
                        updates.overview = {
                          ...currentDeal.overview,
                          website: websiteItem.field_value as string,
                        };
                      }

                      updateDeal(updates);
                    }
                    return baseFormat;
                  }
                  default: {
                    console.log("Entering default case for type:", type);
                    return baseFormat as unknown as
                      | SummaryItem
                      | TearsheetItem
                      | StartupscoreItem
                      | DatasufficiencyItem;
                  }
                }
              });

              if (formattedItems.length > 0) {
                const saveKey =
                  type === "datasufficiencyscores"
                    ? "datasufficiency"
                    : type === "datasufficiencyscores_v2"
                    ? "datasufficiency_v2"
                    : type;
                acc[saveKey] = formattedItems as Array<
                  | SummaryItem
                  | TearsheetItem
                  | StartupscoreItem
                  | DatasufficiencyItem
                  | MetricsReport
                >;
              }
            });

            return acc;
          },
          {} as Record<
            string,
            Array<
              | SummaryItem
              | TearsheetItem
              | StartupscoreItem
              | DatasufficiencyItem
              | MetricsReport
            >
          >
        );

      if (Object.keys(processedReports).length > 0 && currentDeal.id) {
        console.log("Updating deal with processed reports:", processedReports);
        // Update the deal with the processed reports
        updateDeal({
          aiGenerations: {
            ...currentDeal.aiGenerations,
            ...processedReports,
          },
        });
      }

      return completedReports.filter(Boolean);
    },
    enabled:
      !!currentDeal?.id &&
      !!activeWorkspace?._id &&
      !!reportStatus,
  });

  // Failure toasts are now emitted by useReportStatusSocket (event-driven,
  // fires exactly once per status change). The legacy effect that watched
  // polled reportStatus has been removed to avoid duplicate toasts.

  // Sync polled reportStatus to deals list store so dependent
  // components see fresh data
  useEffect(() => {
    if (!reportStatus?.opportunityApplication?.report_status || !currentDeal?.id) return;
    dealsListUpdateDeal(currentDeal.id, {
      reportStatus: {
        opportunityApplication: {
          report_status: reportStatus.opportunityApplication.report_status,
        },
      },
    });
  }, [reportStatus, currentDeal?.id, dealsListUpdateDeal]);

  // Fetch invitation status when deal loads
  const fetchInvitationStatus = useCallback(async () => {
    if (!currentDeal?.id) return;

    try {
      const response = await founderInvitationApi.getCompanyInvitationStatus(
        currentDeal.id
      );
      if (response.status === "success") {
        setInvitationStatus(response.result);
      }
    } catch (error) {
      console.error("Error fetching invitation status:", error);
    }
  }, [currentDeal?.id]);

  useEffect(() => {
    fetchInvitationStatus();
  }, [fetchInvitationStatus]);

  const navigate = useNavigate();
  const handleDealDeleted = useCallback(() => {
    if (activeWorkspace?._id) {
      fetchDealsList(activeWorkspace._id);
      navigate(`/${activeWorkspace._id}/deals`);
    }
  }, [activeWorkspace?._id, fetchDealsList, navigate]);

  const outletContext = {
    isDealsLoading,
    chatOpen,
    setChatOpen,
    currentDeal,
    reportStatus,
    reportData,
    invitationStatus,
    refetchInvitationStatus: fetchInvitationStatus,
    onDealDeleted: handleDealDeleted,
  };

  const { startTour } = useTourStore();
  const isMobileView = window.innerWidth < 768; // Basic mobile detection

  // Start appropriate tour when deal detail page loads
  useEffect(() => {
    if (id) {
      if (isMobileView) {
        startTour(TOUR_TYPES.MOBILE_DEAL_DETAIL);
      } else {
        startTour(TOUR_TYPES.DEAL_DETAIL);
      }
    }
  }, [id, isMobileView, startTour]);

  // Add logs to check current deal and workspace
  useEffect(() => {
    console.log("Current deal:", currentDeal);
    console.log("Active workspace:", activeWorkspace);
  }, [currentDeal, activeWorkspace]);

  // Detect if we're on the new tabbed deal detail page (exact /deals/:id with no sub-path)
  // In that case, skip the deal sidebar and DiligenceGPT sidebar — the page manages its own layout
  const pathAfterDealId = location.pathname.split(`/deals/${id}`)[1] || "";
  const isNewTabbedPage = pathAfterDealId === "" || pathAfterDealId === "/";

  if (isNewTabbedPage) {
    return (
      <BaseLayout
        isDealLayout={false}
        disableMobileTopBar
        outletContext={outletContext}
        isLoading={isDealsLoading && !currentDeal}
        loadingContent={<DealDetailSkeleton />}
      />
    );
  }

  return (
    <BaseLayout
      isDealLayout
      currentDeal={currentDeal || undefined}
      diligenceGptSidebar={
        <DiligencegptSidebar
          side="right"
          collapsible="none"
          className="h-[calc(100%-52px)] w-full bg-[#171717]"
          currentDeal={currentDeal as DealTransformed}
        />
      }
      chatOpen={chatOpen}
      onChatToggle={() => setChatOpen(!chatOpen)}
      mobileMenuOpen={mobileMenuOpen}
      onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
      outletContext={outletContext}
      isLoading={isDealsLoading && !currentDeal}
      loadingContent={<DealDetailSkeleton />}
    />
  );
};
